import { useCallback } from 'react';

export interface HandleRubricAnswerChangeParams {
  /** The entity ID (code ID or case ID). Accepted as string or number; always sent to API as string. */
  entityId: string | number;
  /** The entity type for API calls */
  entityType: CQA.AttributeEntityType;
  /** Current attributes on the entity */
  attributes: CQA.Attribute[];
  /** The question ID being answered */
  questionId: number;
  /** The selected answer value ID (for MULTIPLE_CHOICE/SYSTEM). Exactly one of answerValueId, answerDisplayText, or spanAnnotationIds required. */
  answerValueId?: number;
  /** The free-text response (for FREE_TEXT questions). Exactly one of answerValueId, answerDisplayText, or spanAnnotationIds required. */
  answerDisplayText?: string;
  /** The selected span annotation IDs (for SPAN/SPANS questions). Exactly one of answerValueId, answerDisplayText, or spanAnnotationIds required. */
  spanAnnotationIds?: number[];
  /** IDs of downstream questions whose attributes should be cleared */
  downstreamQuestionIds: number[];
}

export const useHandleRubricAnswerChange = (
  onAnswerChange: (params: CQA.UpdateAttributeParams) => void | Promise<void>,
  onDeleteAnswer: (params: CQA.DeleteAttributeParams) => Promise<void>,
  onClearDownstream?: (params: CQA.ClearDownstreamAttributesParams) => void
) => {
  return useCallback(async (params: HandleRubricAnswerChangeParams) => {
    const {
      entityId,
      entityType,
      attributes,
      questionId,
      answerValueId,
      answerDisplayText,
      spanAnnotationIds,
      downstreamQuestionIds
    } = params;

    const attributesToDelete = (attributes || [])
      .filter(a => downstreamQuestionIds.includes(a.questionId));

    // Optimistically clear all downstream attributes from Redux in a single dispatch.
    // This prevents intermediate renders with partial state while deletes are in-flight.
    if (onClearDownstream && downstreamQuestionIds.length > 0) {
      onClearDownstream({
        entityId: typeof entityId === 'number' ? String(entityId) : entityId,
        entityType,
        questionIds: downstreamQuestionIds
      });
    }

    // Delete each downstream attribute via API
    for (const attribute of attributesToDelete) {
      await onDeleteAnswer({
        entityId: String(entityId),
        entityType,
        questionId: attribute.questionId
      });
    }

    // If SPAN/SPANS question was cleared (empty array), delete the attribute instead of updating
    if (spanAnnotationIds != null && spanAnnotationIds.length === 0) {
      const existingAttribute = (attributes || []).find(a => a.questionId === questionId);
      if (existingAttribute) {
        await onDeleteAnswer({
          entityId: String(entityId),
          entityType,
          questionId
        });
      }
      return;
    }

    // Now save the new attribute (exactly one of answerValueId, answerDisplayText, or spanAnnotationIds)
    const updateParams: CQA.UpdateAttributeParams = {
      entityId: String(entityId),
      entityType,
      questionId
    };
    if (answerDisplayText != null) {
      updateParams.answerDisplayText = answerDisplayText;
    } else if (spanAnnotationIds != null && spanAnnotationIds.length > 0) {
      updateParams.spanAnnotationIds = spanAnnotationIds;
    } else {
      updateParams.answerValueId = answerValueId;
    }
    await onAnswerChange(updateParams);
  }, [onAnswerChange, onDeleteAnswer, onClearDownstream]);
};

export default useHandleRubricAnswerChange;